import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import type {
  AfterViewInit,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  Output,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
  inject,
  DOCUMENT,
} from '@angular/core';
import { RepetitiveSubscription } from '@freelancer/decorators';
import { Subscription, catchError, first, of } from 'rxjs';
import { Assets } from '../assets';
import { AnimatedSvgService } from './animated-svg.service';
import { SvgDisplay, SvgLoadMethod } from './animated-svg.types';

@Component({
  standalone: true,
  selector: 'fl-animated-svg',
  template: `
    <div
      class="SvgContainer"
      #container
    >
      <svg #nativeSvg></svg>
    </div>
  `,
  styleUrls: [
    './animated-svg.component.scss',
    '../spinner/spinner.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnimatedSvgComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  private animatedSvgService = inject(AnimatedSvgService);
  private assets = inject(Assets);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);
  private doc = inject<Document>(DOCUMENT);

  /** SVG filename inside the `assets/animations` folder.
   * Without the `.svg` extension and prefixed by `animation-`.
   * `animations` is allowlisted into the SSR image alongside `icons`.
   */
  @Input() name: string;

  @Input() loadMethod: SvgLoadMethod = 'eager';

  /** Aria label used for accessibility. Similar to the alt text of the picture component. */
  @HostBinding('attr.aria-label')
  @Input()
  label = '';

  @HostBinding('attr.data-display')
  @Input()
  display: SvgDisplay = 'inline-block';

  @Input() clickable = false;

  @Output() svgLoaded = new EventEmitter<boolean>();

  @HostBinding('attr.role')
  svgRole: 'img' | 'button' = 'img';
  @HostBinding('attr.tabindex')
  svgTabIndex?: 0;

  private svgUrl: string;
  @ViewChild('container', { static: true })
  private containerElement: ElementRef<HTMLDivElement>;
  @ViewChild('nativeSvg', { static: true })
  private svgElement: ElementRef<Element>;
  @RepetitiveSubscription()
  private svgSubscription?: Subscription;
  private intersectionObserver?: IntersectionObserver;
  private withinView = false;

  ngOnChanges(changes: SimpleChanges): void {
    if ('name' in changes) {
      this.svgUrl = this.assets.getUrl(
        `animations/${changes.name.currentValue}.svg`,
      );
      if (
        this.loadMethod === 'preload' ||
        this.loadMethod === 'eager' ||
        (this.loadMethod === 'lazy' && this.withinView)
      ) {
        this.loadSvg();
      }
    }

    if ('clicklable' in changes) {
      if (changes.clickable.currentValue) {
        this.svgRole = 'button';
        this.svgTabIndex = 0;
      } else {
        this.svgRole = 'img';
        this.svgTabIndex = undefined;
      }
    }
  }

  ngOnInit(): void {
    if (this.loadMethod === 'preload') {
      this.addPreloadImageLink(this.svgUrl);
    }
  }

  ngAfterViewInit(): void {
    if (this.loadMethod === 'lazy') {
      const config = {
        rootMargin: '100%',
      };
      if (isPlatformBrowser(this.platformId)) {
        this.intersectionObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio === 0) {
              return;
            }
            this.loadSvg();
            this.intersectionObserver?.unobserve(
              this.containerElement.nativeElement,
            );
            this.intersectionObserver = undefined;
            this.withinView = true;
          });
        }, config);
        this.intersectionObserver.observe(this.containerElement.nativeElement);
      }
    }
  }

  ngOnDestroy(): void {
    this.svgSubscription?.unsubscribe();
    this.intersectionObserver?.unobserve(this.containerElement.nativeElement);
  }

  private loadSvg(): void {
    this.svgSubscription = this.animatedSvgService
      .getAnimatedSvg(this.svgUrl, this.renderer)
      .pipe(
        catchError((err: Error) => {
          // put a placeholder for network errors
          if (err instanceof HttpErrorResponse) {
            return of(this.createPlaceholder());
          }
          throw err;
        }),
        first(),
      )
      .subscribe(element => {
        if (this.containerElement) {
          this.setElement(element);
          this.svgLoaded.emit(true);
        }
      });
  }

  private setElement(element: Element): void {
    const nativeContainer = this.containerElement.nativeElement;
    const nativeSvgElement = this.svgElement.nativeElement;
    this.renderer.removeChild(nativeContainer, nativeSvgElement);
    this.renderer.appendChild(nativeContainer, element);
    this.svgElement.nativeElement = element;
    this.reinsertScript(element);
  }

  /**
   * Reinsert the script tag to play the animations
   */
  private reinsertScript(element: Element): void {
    const oldScript = element.querySelector('script') as HTMLScriptElement;
    if (oldScript?.textContent) {
      const newScript = this.renderer.createElement(
        'script',
      ) as HTMLScriptElement;
      newScript.textContent = oldScript.textContent;

      this.renderer.removeChild(element, oldScript);
      this.renderer.appendChild(element, newScript);
    }
  }

  private addPreloadImageLink(src: string, mediaQuery?: string): void {
    const preloadLink = this.renderer.createElement('link');
    preloadLink.setAttribute('rel', 'preload');
    preloadLink.setAttribute('href', src);
    preloadLink.setAttribute('as', 'image');
    if (mediaQuery) {
      preloadLink.setAttribute('media', mediaQuery);
    }
    this.doc.head.appendChild(preloadLink);
  }

  /**
   * Creates a spinner using the CSS from the spinner component.
   * @returns A div with a spinner
   */
  private createPlaceholder(): HTMLDivElement {
    const spinnerDiv = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.addClass(spinnerDiv, 'Spinner');
    this.renderer.setAttribute(spinnerDiv, 'role', 'presentation');
    this.renderer.setAttribute(spinnerDiv, 'data-type', 'default');
    this.renderer.setAttribute(spinnerDiv, 'data-size', 'mid');
    const containerDiv = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.addClass(containerDiv, 'LoadingSpinnerContainer');
    this.renderer.appendChild(containerDiv, spinnerDiv);
    return containerDiv;
  }
}
